import { emit, useApi, useSilentApi } from '@/api/useApi'
import API from '@/api/apiUrls'
import { defineStore } from 'pinia'
import { useBaseStore } from '@haskoning/vue-basestore'
import { DEFAULT_VM_MAX_RUNTIME_SECONDS } from '@/stores/type/calculation.type'
import type {
  Calculation,
  CalculationInputUpload,
  CalculationComputeResourceLogsResponse,
  CalculationFormPayload,
  CalculationReschedulePayload,
  ContainerImageOption,
  CalculationComputeResource,
  CalculationResultFile,
  CalculationResultFilesResponse,
  CalculationStorageCost,
  OutputAccessDuration,
  OutputAccessLink
} from '@/stores/type/calculation.type'
import { computed, ref } from 'vue'
import { useAuthorizationStore } from '@/stores/authorization'
import type { CalculationProgressInfo } from '@/components/calculation-result/type/type'
import { useSelectedItemMixin } from '@/stores/useSelectedItemMixin'
import { downloadSignedFile, uploadBlobInBlocks, waitForTransfer } from '@/api/blobTransfers'

type ArchiveDownload = {
  archive_id: string
  status: 'PENDING' | 'RUNNING' | 'READY' | 'FAILED'
  download_url?: string
  detail?: string
  retry_after?: number
}

const { api } = useApi()
const { api: silentApi } = useSilentApi()

const baseUrl = API.CORE.CALCULATIONS.INDEX
const containerImagesUrl = API.CORE.CALCULATIONS.CONTAINER_IMAGES
const getCeleryCalculateUrl = API.CORE.CALCULATIONS.CELERY_CALCULATE
const getRescheduleUrl = API.CORE.CALCULATIONS.RESCHEDULE
const getStopUrl = API.CORE.CALCULATIONS.STOP
const getComputeResourcesUrl = API.CORE.CALCULATIONS.GET_COMPUTE_RESOURCES
const getHistoricalComputeResourcesUrl = API.CORE.CALCULATIONS.GET_HISTORICAL_COMPUTE_RESOURCES
const getResultFilesUrl = API.CORE.CALCULATIONS.GET_RESULT_FILES
const getComputeResourceLogsUrl = API.CORE.CALCULATIONS.GET_COMPUTE_RESOURCE_LOGS
const getDownloadComputeResourceLogsUrl = API.CORE.CALCULATIONS.DOWNLOAD_COMPUTE_RESOURCE_LOGS
const getDownloadResultFileUrl = API.CORE.CALCULATIONS.DOWNLOAD_RESULT_FILE
const getDeleteResultFileUrl = API.CORE.CALCULATIONS.DELETE_RESULT_FILE
const getDownloadResultFilesZipUrl = API.CORE.CALCULATIONS.DOWNLOAD_RESULT_FILES_ZIP
const getDeleteResultFolderUrl = API.CORE.CALCULATIONS.DELETE_RESULT_FOLDER
const getCalculationProgressUrl = API.CORE.CALCULATIONS.GET_CALCULATION_PROGRESS

export const useCalculationStore = defineStore('calculation', () => {
  const authorizationStore = useAuthorizationStore()

  const canAddCalculation = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['add_calculation']
    })
  })

  const canDeleteCalculation = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['delete_calculation']
    })
  })

  const canStopCalculation = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['change_calculation', 'delete_calculation']
    })
  })

  const baseStore = useBaseStore<Calculation>({ service: baseUrl, api })
  const selectedItemMixin = useSelectedItemMixin(baseStore)

  const resultFiles = ref<CalculationResultFile[]>([])
  const resultStorageCost = ref<CalculationStorageCost | null>(null)
  const resultFilesLoading = ref(false)
  const resultFileDownloadLoading = ref<string | null>(null)
  const resultFilesZipDownloadLoading = ref<string | null>(null)
  const resultFileDeleteLoading = ref<string | null>(null)
  const resultFolderDeleteLoading = ref<string | null>(null)
  const zipFileDownloadLoading = ref<number | null>(null)
  const rescheduleLoading = ref<number | null>(null)
  const stopLoading = ref<number | null>(null)
  const computeResources = ref<CalculationComputeResource[]>([])
  const historicalComputeResources = ref<CalculationComputeResource[]>([])
  const computeResourcesLoading = ref(false)
  const historicalComputeResourcesLoading = ref(false)
  const computeResourceLogsLoading = ref(false)
  const computeResourceLogDownloadLoading = ref<string | null>(null)
  const containerImages = ref<ContainerImageOption[]>([])
  const containerImagesLoading = ref(false)
  const containerImagesError = ref<string | null>(null)
  const inputUploadProgress = ref(0)
  const inputUploadMessage = ref('')
  const inputUploadError = ref('')
  const resultDownloadError = ref('')
  let inputUploadController: AbortController | null = null
  let resultArchiveController: AbortController | null = null

  const transferGet = async <T>(url: string): Promise<T> => {
    await silentApi.apiService.authenticationHandler.setAccessTokenInHeaderFromStore()
    return (await silentApi.service(url).get()) as T
  }

  const transferPost = async <T>(url: string, body: unknown): Promise<T> => {
    await silentApi.apiService.authenticationHandler.setAccessTokenInHeaderFromStore()
    return (await silentApi.service(url).post(body)) as T
  }

  const transferErrorMessage = async (error: unknown, fallback: string) => {
    if (error instanceof Response) {
      try {
        const data = await error.clone().json()
        if (typeof data.detail === 'string') return data.detail
      } catch {
        /* Use the fallback when the server returned no JSON. */
      }
    }
    return error instanceof Error ? error.message : fallback
  }

  const rememberCalculation = (calculation: Calculation) => {
    const index = baseStore.items.value.findIndex((item) => item.id === calculation.id)
    if (index >= 0) baseStore.items.value[index] = calculation
    else baseStore.addItems([calculation])
    baseStore.setCurrentItem(calculation)
  }

  const filterFields = ref(['name', 'note'])

  const fieldsFiltered = computed(() => {
    return baseStore.fieldsList.value.filter((field) =>
      filterFields.value.includes(field.parameter)
    )
  })

  const createNewCalculation = (project: number) => {
    inputUploadError.value = ''
    inputUploadMessage.value = ''
    inputUploadProgress.value = 0
    const newCalculation = {
      project,
      name: '',
      note: '',
      container_image: '',
      vm_max_runtime_seconds: DEFAULT_VM_MAX_RUNTIME_SECONDS,
      hardware_profile: null,
      favorite: false,
      status: 'DRAFT'
    } as Calculation

    return newCalculation
  }

  const saveCalculationForm = async (item: CalculationFormPayload) => {
    if (baseStore.writeLoading.value) return false
    const controller = new AbortController()
    inputUploadController = controller
    inputUploadError.value = ''
    inputUploadProgress.value = 0
    try {
      baseStore.writeLoading.value = true
      inputUploadMessage.value = 'Saving calculation…'
      let calculation = baseStore.currentItem.value?.id
        ? await transferGet<Calculation>(`${baseUrl}${baseStore.currentItem.value.id}/`)
        : await transferPost<Calculation>(baseUrl, {
            project: item.project,
            hardware_profile: item.hardwareProfile,
            name: item.name,
            note: item.note ?? '',
            container_image: item.containerImage ?? '',
            vm_max_runtime_seconds: item.vmMaxRuntimeSeconds
          })
      controller.signal.throwIfAborted()
      rememberCalculation(calculation)
      if (!calculation.input_ready && calculation.input_upload_status !== 'VALIDATING') {
        const file = item.zipFile
        if (!file) throw new Error('Select the input ZIP to complete this draft calculation.')
        const beginUpload = (uploadId?: string) =>
          transferPost<CalculationInputUpload>(
            API.CORE.CALCULATIONS.BEGIN_INPUT_UPLOAD(calculation.id),
            {
              upload_id: uploadId
            }
          )
        const upload = calculation.input_upload ?? (await beginUpload())
        inputUploadMessage.value = `Uploading ${file.name}…`
        await uploadBlobInBlocks(
          file,
          upload.upload_url,
          async () => (await beginUpload(upload.upload_id)).upload_url,
          (percentage) => {
            inputUploadProgress.value = percentage
          },
          controller.signal
        )
        controller.signal.throwIfAborted()
        calculation = await transferGet<Calculation>(`${baseUrl}${calculation.id}/`)
      }
      inputUploadMessage.value =
        'Checking input file… You can close this dialog and continue later.'
      const deadline = Date.now() + 30 * 60 * 1000
      while (
        !calculation.input_ready ||
        (calculation.input_auto_start && !calculation.task_id && calculation.status === 'DRAFT')
      ) {
        rememberCalculation(calculation)
        if (calculation.input_upload_status === 'FAILED') {
          throw new Error(calculation.input_upload_error || 'The input ZIP could not be validated.')
        }
        if (calculation.input_ready)
          inputUploadMessage.value =
            'Starting calculation… You can close this dialog and continue later.'
        if (Date.now() > deadline)
          throw new Error('Input validation is still running. Continue from the saved draft later.')
        await waitForTransfer(2000, controller.signal)
        calculation = await transferGet<Calculation>(`${baseUrl}${calculation.id}/`)
      }
      controller.signal.throwIfAborted()
      rememberCalculation(calculation)
      inputUploadMessage.value = ''
      return calculation
    } catch (error) {
      inputUploadError.value =
        error instanceof DOMException && error.name === 'AbortError'
          ? 'The calculation is saved as a draft. You can continue uploading later.'
          : await transferErrorMessage(error, 'The input upload failed. Your draft has been saved.')
      return false
    } finally {
      baseStore.writeLoading.value = false
      inputUploadController = null
    }
  }

  const cancelInputUpload = () => inputUploadController?.abort()

  const celeryCalculate = async (item: Calculation) => {
    try {
      const response = await api.service(getCeleryCalculateUrl(item.id)).post(item)
      if (!response) return null

      const index = baseStore.items.value.findIndex(
        (calculation: Calculation) => calculation.id === response.id
      )
      if (index >= 0) {
        baseStore.items.value[index] = response
      }
      if (baseStore.currentItem.value?.id === response.id) {
        baseStore.setCurrentItem(response)
      }
      return response as Calculation
    } catch (e) {
      console.error(e)
      return null
    }
  }

  const rescheduleCalculation = async (
    item: Calculation,
    payload?: CalculationReschedulePayload
  ) => {
    rescheduleLoading.value = item.id
    try {
      const response = await api.service(getRescheduleUrl(item.id)).post({
        container_image: payload?.containerImage ?? item.container_image ?? '',
        hardware_profile: payload?.hardwareProfile ?? item.hardware_profile,
        vm_max_runtime_seconds:
          payload?.vmMaxRuntimeSeconds ??
          item.vm_max_runtime_seconds ??
          DEFAULT_VM_MAX_RUNTIME_SECONDS
      })
      if (!response) return null

      const index = baseStore.items.value.findIndex(
        (calculation: Calculation) => calculation.id === response.id
      )
      if (index >= 0) {
        baseStore.items.value[index] = response
      }
      if (baseStore.currentItem.value?.id === response.id) {
        baseStore.setCurrentItem(response)
      }
      return response as Calculation
    } catch (e) {
      console.error(e)
      return null
    } finally {
      rescheduleLoading.value = null
    }
  }

  const stopCalculation = async (item: Calculation) => {
    stopLoading.value = item.id
    try {
      const response = await api.service(getStopUrl(item.id)).post(item)
      if (!response) return null

      const index = baseStore.items.value.findIndex(
        (calculation: Calculation) => calculation.id === response.id
      )
      if (index >= 0) {
        baseStore.items.value[index] = response
      }
      if (baseStore.currentItem.value?.id === response.id) {
        baseStore.setCurrentItem(response)
      }
      return response as Calculation
    } catch (e) {
      console.error(e)
      return null
    } finally {
      stopLoading.value = null
    }
  }

  const fetchContainerImages = async () => {
    containerImagesLoading.value = true
    containerImagesError.value = null
    try {
      containerImages.value = (await api
        .service(containerImagesUrl)
        .get()) as ContainerImageOption[]
      return containerImages.value
    } catch (e) {
      console.error(e)
      containerImagesError.value = apiErrorMessage(e)
      containerImages.value = []
      return []
    } finally {
      containerImagesLoading.value = false
    }
  }

  const apiErrorMessage = (error: unknown) => {
    const candidate = error as {
      message?: string
      response?: {
        data?: {
          detail?: string
        }
      }
    }

    return (
      candidate?.response?.data?.detail ||
      candidate?.message ||
      'Could not load calculation images.'
    )
  }

  const getCalculationProgress = async (
    id: number
  ): Promise<CalculationProgressInfo | undefined> => {
    try {
      return await api.service(getCalculationProgressUrl(id)).get()
    } catch (e) {
      console.error(e)
      return undefined
    }
  }

  const getComputeResources = async (id: number): Promise<CalculationComputeResource[]> => {
    computeResourcesLoading.value = true
    try {
      computeResources.value = (await api
        .service(getComputeResourcesUrl(id))
        .get()) as CalculationComputeResource[]
      return computeResources.value
    } catch (e) {
      console.error(e)
      computeResources.value = []
      return []
    } finally {
      computeResourcesLoading.value = false
    }
  }

  const getHistoricalComputeResources = async (
    id: number
  ): Promise<CalculationComputeResource[]> => {
    historicalComputeResourcesLoading.value = true
    try {
      historicalComputeResources.value = (await api
        .service(getHistoricalComputeResourcesUrl(id))
        .get()) as CalculationComputeResource[]
      return historicalComputeResources.value
    } catch (e) {
      console.error(e)
      historicalComputeResources.value = []
      return []
    } finally {
      historicalComputeResourcesLoading.value = false
    }
  }

  const getResultFiles = async (id: number): Promise<CalculationResultFile[]> => {
    resultFilesLoading.value = true
    try {
      const response = (await api
        .service(getResultFilesUrl(id))
        .get()) as CalculationResultFilesResponse
      resultFiles.value = response?.files ?? []
      resultStorageCost.value = response?.storage_cost ?? null
      return resultFiles.value
    } catch (e) {
      console.error(e)
      resultFiles.value = []
      resultStorageCost.value = null
      return []
    } finally {
      resultFilesLoading.value = false
    }
  }

  const getComputeResourceLogs = async (
    calculationId: number,
    computeResourceName: string,
    tail = 500
  ): Promise<CalculationComputeResourceLogsResponse | null> => {
    computeResourceLogsLoading.value = true
    try {
      return (await api
        .service(getComputeResourceLogsUrl(calculationId, computeResourceName, tail))
        .get()) as CalculationComputeResourceLogsResponse
    } catch (e) {
      console.error(e)
      return null
    } finally {
      computeResourceLogsLoading.value = false
    }
  }

  const downloadComputeResourceLogs = async (
    calculationId: number,
    computeResourceName: string
  ) => {
    computeResourceLogDownloadLoading.value = computeResourceName
    try {
      await downloadBlobWithFilename(
        getDownloadComputeResourceLogsUrl(calculationId, computeResourceName),
        `${computeResourceName}.log`
      )
    } catch (e) {
      console.error(e)
    } finally {
      computeResourceLogDownloadLoading.value = null
    }
  }

  const downloadResultFile = async (calculationId: number, file: CalculationResultFile) => {
    resultFileDownloadLoading.value = file.blob_name
    resultDownloadError.value = ''
    try {
      const response = await transferGet<{ download_url: string }>(
        getDownloadResultFileUrl(calculationId, file.blob_name)
      )
      downloadSignedFile(response.download_url)
    } catch (error) {
      resultDownloadError.value = await transferErrorMessage(
        error,
        'The file could not be downloaded. Please try again.'
      )
    } finally {
      resultFileDownloadLoading.value = null
    }
  }

  const createOutputAccessLink = async (
    calculationId: number,
    duration: OutputAccessDuration,
    path: string[]
  ): Promise<OutputAccessLink> => {
    try {
      return await transferPost<OutputAccessLink>(
        API.CORE.CALCULATIONS.CREATE_OUTPUT_ACCESS_LINK(calculationId),
        { duration, path: path.join('/') }
      )
    } catch (error) {
      throw new Error(
        await transferErrorMessage(error, 'The access link could not be created. Please try again.')
      )
    }
  }

  const downloadResultFilesZip = async (calculationId: number, pathParts: string[] = []) => {
    if (resultFilesZipDownloadLoading.value) return
    const path = pathParts.join('/')
    resultFilesZipDownloadLoading.value = path || '__root__'
    resultDownloadError.value = ''
    const controller = new AbortController()
    resultArchiveController = controller
    try {
      let archive = await transferGet<ArchiveDownload>(
        getDownloadResultFilesZipUrl(calculationId, path)
      )
      const deadline = Date.now() + 30 * 60 * 1000
      while (archive.status === 'PENDING' || archive.status === 'RUNNING') {
        if (Date.now() > deadline)
          throw new Error('ZIP preparation is taking longer than expected. Please try again later.')
        await waitForTransfer((archive.retry_after ?? 2) * 1000, controller.signal)
        archive = await transferGet<ArchiveDownload>(
          API.CORE.CALCULATIONS.RESULT_FILES_ZIP_STATUS(calculationId, archive.archive_id)
        )
      }
      if (!archive.download_url)
        throw new Error(archive.detail || 'The ZIP could not be prepared. Please try again.')
      if (controller.signal.aborted) return
      downloadSignedFile(archive.download_url)
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      resultDownloadError.value = await transferErrorMessage(
        error,
        'The ZIP could not be downloaded. Please try again.'
      )
    } finally {
      resultFilesZipDownloadLoading.value = null
      resultArchiveController = null
    }
  }

  const deleteResultFile = async (calculationId: number, file: CalculationResultFile) => {
    resultFileDeleteLoading.value = file.blob_name
    try {
      await api.apiService.api(getDeleteResultFileUrl(calculationId, file.blob_name), 'DELETE')
      await getResultFiles(calculationId)
      return true
    } catch (e) {
      console.error(e)
      return false
    } finally {
      resultFileDeleteLoading.value = null
    }
  }

  const deleteResultFolder = async (calculationId: number, pathParts: string[]) => {
    const path = pathParts.join('/')
    resultFolderDeleteLoading.value = path || '__root__'
    try {
      await api.apiService.api(getDeleteResultFolderUrl(calculationId, path), 'DELETE')
      await getResultFiles(calculationId)
      return true
    } catch (e) {
      console.error(e)
      return false
    } finally {
      resultFolderDeleteLoading.value = null
    }
  }

  const downloadZipFile = async (calculation: Calculation) => {
    zipFileDownloadLoading.value = calculation.id
    try {
      const response = await transferGet<{ download_url: string }>(
        API.CORE.CALCULATIONS.INPUT_FILE_DOWNLOAD_URL(calculation.id)
      )
      downloadSignedFile(response.download_url)
    } catch (error) {
      emit('error', {
        status: 500,
        statusText: await transferErrorMessage(
          error,
          'The input file could not be downloaded. Please try again.'
        )
      })
    } finally {
      zipFileDownloadLoading.value = null
    }
  }

  const moveSelectedItemsToArchive = () => {
    console.log('move-selected-items-to-archive')
  }

  const deleteSelectedItems = () => {
    baseStore.openDeleteDialog()
  }

  const downloadBlobWithFilename = async (url: string, filename: string) => {
    const blob = (await silentApi.service(url).getBlob()) as Blob
    const objectUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(objectUrl)
  }

  function resetAll() {
    cancelInputUpload()
    resultArchiveController?.abort()
    inputUploadError.value = ''
    resultDownloadError.value = ''
    baseStore.reset()
    resultFiles.value = []
    resultStorageCost.value = null
    resultFilesLoading.value = false
    resultFileDownloadLoading.value = null
    resultFilesZipDownloadLoading.value = null
    resultFileDeleteLoading.value = null
    resultFolderDeleteLoading.value = null
    zipFileDownloadLoading.value = null
    rescheduleLoading.value = null
    stopLoading.value = null
    computeResources.value = []
    historicalComputeResources.value = []
    computeResourcesLoading.value = false
    historicalComputeResourcesLoading.value = false
    computeResourceLogsLoading.value = false
    computeResourceLogDownloadLoading.value = null
    containerImages.value = []
    containerImagesLoading.value = false
    containerImagesError.value = null
  }

  return {
    ...baseStore,
    ...selectedItemMixin,
    canAddCalculation,
    canDeleteCalculation,
    canStopCalculation,
    fieldsFiltered,
    createNewCalculation,
    saveCalculationForm,
    cancelInputUpload,
    inputUploadProgress,
    inputUploadMessage,
    inputUploadError,
    resultDownloadError,
    celeryCalculate,
    rescheduleCalculation,
    stopCalculation,
    resultFiles,
    resultStorageCost,
    resultFilesLoading,
    resultFileDownloadLoading,
    resultFilesZipDownloadLoading,
    resultFileDeleteLoading,
    resultFolderDeleteLoading,
    zipFileDownloadLoading,
    rescheduleLoading,
    stopLoading,
    computeResources,
    historicalComputeResources,
    computeResourcesLoading,
    historicalComputeResourcesLoading,
    computeResourceLogsLoading,
    computeResourceLogDownloadLoading,
    containerImages,
    containerImagesLoading,
    containerImagesError,
    fetchContainerImages,
    getCalculationProgress,
    getComputeResources,
    getHistoricalComputeResources,
    getResultFiles,
    getComputeResourceLogs,
    downloadComputeResourceLogs,
    downloadResultFile,
    createOutputAccessLink,
    downloadResultFilesZip,
    deleteResultFile,
    deleteResultFolder,
    downloadZipFile,
    moveSelectedItemsToArchive,
    deleteSelectedItems,
    resetAll
  }
})
